import { TableColumn } from '@backstage/core-components';
import { CatalogTableRow } from '@backstage/plugin-catalog';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import { annotationValue } from './utils';

const VERSION_ANNOTATION = 'kubernetes.io/version';
const LOCATION_ANNOTATION = 'azure.com/location';
const SUBSCRIPTION_ID_ANNOTATION = 'azure.com/subscription-id';

function specString(entity: CatalogTableRow['entity'], key: string): string {
  const value = (entity.spec as Record<string, unknown> | undefined)?.[key];
  return typeof value === 'string' && value ? value : '—';
}

function narrow(width: number) {
  const style = { width, maxWidth: width, whiteSpace: 'nowrap' as const };
  return { width: `${width}px`, cellStyle: style, headerStyle: style };
}

export const kubernetesClusterColumns: TableColumn<CatalogTableRow>[] = [
  {
    title: 'Name',
    field: 'resolved.entityRef',
    highlight: true,
    render: ({ entity }) => (
      <EntityRefLink entityRef={entity} defaultKind="resource">
        {entity.metadata.title ?? entity.metadata.name}
      </EntityRefLink>
    ),
  },
  {
    title: 'Version',
    field: `entity.metadata.annotations.${VERSION_ANNOTATION}`,
    ...narrow(90),
    render: ({ entity }) => annotationValue(entity, VERSION_ANNOTATION),
  },
  {
    title: 'Location',
    field: `entity.metadata.annotations.${LOCATION_ANNOTATION}`,
    ...narrow(120),
    render: ({ entity }) => annotationValue(entity, LOCATION_ANNOTATION),
  },
  {
    title: 'Power State',
    field: 'entity.spec.powerState',
    ...narrow(110),
    render: ({ entity }) => specString(entity, 'powerState'),
  },
  {
    title: 'Owner',
    field: 'entity.spec.owner',
    render: ({ entity }) => {
      const owner = (entity.spec as { owner?: unknown } | undefined)?.owner;
      if (typeof owner !== 'string' || !owner || owner === 'unknown') {
        return <>—</>;
      }
      return (
        <EntityRefLink
          entityRef={{ kind: 'Group', namespace: 'default', name: owner }}
        />
      );
    },
  },
  {
    title: 'Subscription',
    field: `entity.metadata.annotations.${SUBSCRIPTION_ID_ANNOTATION}`,
    render: ({ entity }) => {
      const subscriptionId =
        entity.metadata.annotations?.[SUBSCRIPTION_ID_ANNOTATION];
      if (!subscriptionId) {
        return <>—</>;
      }
      return (
        <EntityRefLink
          entityRef={`resource:default/subscription-${subscriptionId}`}
          defaultKind="resource"
        >
          {subscriptionId}
        </EntityRefLink>
      );
    },
  },
];
